import { Textarea } from '@/components/shadcn/textarea'
import { useEffect, useRef, useState } from 'react'
import { ConfirmClearDialog } from '../Chat/ConfirmClearDialog'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { cn } from '@/lib/utils'
import { Button } from '@/components/shadcn/button'
import { useChat } from '../Chat/hooks'
import { Send } from 'lucide-react'

// Chat input component
export const LiveChatInput: React.FC<{
  samplePrompts?: string[]
  className?: string
  sendMessage?: (message: string) => unknown | Promise<unknown>
  isLoading?: boolean
  disabled?: boolean
}> = ({ samplePrompts, className, sendMessage: sendMessageProp, isLoading: isLoadingProp, disabled: disabledProp }) => {
  const { messages, sendMessage: sendMessageHook, isLoading: isLoadingHook } = useChat()
  const effectiveSendMessage = sendMessageProp ?? sendMessageHook
  const effectiveIsLoading = isLoadingProp ?? isLoadingHook
  const effectiveDisabled = disabledProp ?? effectiveIsLoading
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inputValue.trim() && !effectiveIsLoading) {
      // Fire and forget; caller handles errors
      void effectiveSendMessage(inputValue.trim())
      setInputValue('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  })

  return (
    <div className={cn('sticky bottom-0 px-4 py-2', className)}>
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1">
            <Textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask anything..."
              disabled={effectiveDisabled}
              // className="min-h-24 flex-1"
              className="min-h-[40px] flex-1 rounded-3xl pl-10 pr-4 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              rows={1}
              autoFocus
            />
            <IconPlusOutline size={16} className="absolute left-3 top-3 text-muted-foreground" />
          </div>
          <Button
            type="submit"
            size="icon"
            disabled={effectiveDisabled || !inputValue.trim()}
            className="h-10 w-10 shrink-0 rounded-full"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        {samplePrompts && messages.length === 0 && !effectiveIsLoading && (
          <div>
            <p className="pt-2 text-center text-base font-light italic text-muted-foreground">
              or try one of these prompts...
            </p>
            <div className="flex w-full flex-wrap gap-1 pt-1">
              {samplePrompts.map((prompt) => (
                <Button
                  variant="outline"
                  className="bg-offwhite/50 rounded-xl !italic text-foreground/70 hover:bg-background"
                  onClick={() => setInputValue(prompt)}
                  size="sm"
                  key={prompt}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <ConfirmClearDialog />
        </div>
      </form>
    </div>
  )
}
